import { ECartTypeId } from '@ringana/configs';
import { ErrorCodes } from '@ringana/errors';
import { IApiAddCartItemRequestBody } from '@/common/interfaces/api.interface';
import {
  ICreateShoppingCart,
  ICustomerCoupon,
  IShoppingCart,
  IShoppingCartIndividual,
  IShoppingCartItem,
} from '@/common/interfaces/cart.interface';
import { ICustomerAddress } from '@/common/interfaces/customers.interface';
import { IProduct } from '@/common/interfaces/products.interface';
import { IPatchCartItemRequestData } from '@/common/interfaces/request.interface';
import { IAddressData } from '@/common/interfaces/shared.interface';
import { TCartItemType } from '@/common/types/index.types';
import ApplicationError from '@/errors/application-error';
import FetchError from '@/errors/fetch-error';
import AddressHelper from '@/helpers/AddressHelper';
import CartHelper from '@/helpers/CartHelper';
import CookieHelper from '@/helpers/CookieHelper';
import CustomerHelper from '@/helpers/CustomerHelper';
import DataLayerHelper from '@/helpers/DataLayerHelper';
import DomEventHelper from '@/helpers/DomEventHelper';
import ErrorHandlerHelper from '@/helpers/ErrorHandlerHelper';
import LocalStorageHelper from '@/helpers/LocalStorageHelper';
import ProductHelper from '@/helpers/ProductHelper';
import SessionStorageHelper from '@/helpers/SessionStorageHelper';
import TrackingHelper from '@/helpers/TrackingHelper';
import TransformHelper from '@/helpers/TransformHelper';
import config from '../../config';
import ZApiService from './ZApiService';

// TODO WEB-3452 ... refactor error handling everywhere in this file!

export default class CartService {
  private zApiService: ZApiService;
  private cart: IShoppingCart | null = null;
  private cartRetriedLoadingOnce = false;

  constructor(zApiService: ZApiService) {
    this.zApiService = zApiService;

    if(LocalStorageHelper.isCartToBeReloaded()) {
      LocalStorageHelper.removeCartToBeReloaded();
      // can't reload directly. have to wait for the circular dependency
      setTimeout(() => this.fetchCart(), 500);
    }
  }

  public async addItemToCart(body: IApiAddCartItemRequestBody, cartId?: number, cartTypeId = ECartTypeId.PERSONAL_USE): Promise<IShoppingCart & { cartContentId?: number }> {
    let internalCartId = cartId;

    if(!CookieHelper.getCartSession() && !CookieHelper.getAuthToken()) {
      await this.createNewCart();
      internalCartId = CookieHelper.getLastUsedCartId();
    }

    if(!internalCartId || +internalCartId <= 0) {
      internalCartId = CookieHelper.getLastUsedCartId();
    } else {
      switch(cartTypeId) {
        case config.cartTypes.EIGENBEDARF:
          CookieHelper.setPersonalUseCartId(internalCartId);
          CookieHelper.setLastUsedCartId(internalCartId);
          break;
        case config.cartTypes.FREMDBEDARF:
          CookieHelper.setLastUsedCartId(internalCartId);
          break;
        case config.cartTypes.PARTNERAPPLICATION:
          // do nothing, PA is not important enough to be stored
          break;
        default:
          console.warn(`cartTypeId '${cartTypeId}' unknown!`);
      }
    }

    try {
      const shoppingCart = await this.zApiService.addItemToCart(body, cartId);

      const cart = shoppingCart.shoppingCarts.find(singleShoppingCart => singleShoppingCart.cartId === internalCartId);
      const item = cart?.items.find((shoppingCartItem): boolean => shoppingCartItem.matchcode.toUpperCase() === body.matchcode.toUpperCase());
      if(item) {
        TrackingHelper.trackCartAddItem(
          item.matchcode,
          item.title ?? ``,
          ``,
          parseFloat(item.price.normal.toFixed(2)),
          body.amount ?? -1,
          shoppingCart.sum.total ? parseFloat(shoppingCart.sum.total.toFixed(2)) : -1,
        );
      }

      CartHelper.updateLocalCart(shoppingCart.shoppingCarts[0]);

      const cartIcon = document.querySelector(`.cartIcon`);
      if(cartIcon) {
        cartIcon.classList.add(`shake is-animating`);
        setTimeout(() => cartIcon.classList.remove(`shake is-animating`), 200);
      }

      // retry enabled again
      this.cartRetriedLoadingOnce = false;

      if(CustomerHelper.getLoggedInState()) {
        const updatedCart = shoppingCart.shoppingCarts[0];
        let firstBuyerProductWithCoupon: { coupon: ICustomerCoupon; item: IShoppingCartItem; updatedCart: IShoppingCartIndividual } | null = null;
        if(updatedCart) {
          try {
            const coupons = await this.fetchCoupons(updatedCart.cn, true);
            for(const updatedCartItem of updatedCart.items) {
              const couponWithMatchcode = coupons.find(coupon => coupon.matchcode === updatedCartItem.matchcode);
              if(couponWithMatchcode) {
                firstBuyerProductWithCoupon = {
                  coupon: couponWithMatchcode,
                  item: updatedCartItem,
                  updatedCart,
                };
              }
            }
          } catch (err) {
            ErrorHandlerHelper.handleError(err, { logToConsole: true });
          }
        }

        if(firstBuyerProductWithCoupon) {
          await this.removeItemFromCart(firstBuyerProductWithCoupon.updatedCart.cartId, firstBuyerProductWithCoupon.item.cartContentId, body.matchcode, false, `coupon`);
          await this.redeemCoupon(firstBuyerProductWithCoupon.updatedCart.cartId, firstBuyerProductWithCoupon.coupon.code);
          DomEventHelper.fireEvent({ name: `rin-add-autoredeemed-coupon` });
        }
      }

      return shoppingCart;
    } catch (err) {
      if(err instanceof Error) {
        if(new RegExp(`^Dieser Artikel kann maximal `, `ig`).test(err.message)) {
          // TODO WEB-3452 - totally refactoring with errorcodes from infoworx !!!
          const regexp = /^Dieser Artikel kann maximal ([0-9]*)-/ig;
          const dataArr = regexp.exec(err.message) ?? [``, ``];
          throw new ApplicationError(`${window.vueCartHelperTexts.errorMaxUnitsPerCart.part1}${dataArr[1]}${window.vueCartHelperTexts.errorMaxUnitsPerCart.part2}`);
        } else if(err instanceof FetchError && err.errorCode === ErrorCodes.LIMITING_BOUNCER_ACTIVE) {
          DomEventHelper.fireEvent({ name: `rin-limiting-shopping-disallowed` });
          DomEventHelper.fireEvent({ detail: { ...body, cartId: internalCartId }, name: `rin-limiting-add2cart-retry` });
          throw err;
        } else {
          ErrorHandlerHelper.handleError(err, { logToConsole: true });
          // TODO WEB-3452 : Refactor to errorCodes
          switch(err.message) {
            case `<idwarenkorb> invalid`:
              /**
               * cart not known or invalid - try again, lets fetch a new cart
               */
              CookieHelper.removeCartId();
              CookieHelper.removeCartSession();
              CookieHelper.removeCartIdsForCheckout();
              if(!this.cartRetriedLoadingOnce) {
                this.cartRetriedLoadingOnce = true;
                await this.createNewCart();
                await this.addItemToCart(body, cartId);
              } else {
                await window.$swal(``, window.vueCartHelperTexts.errorCannotBeAddedTemporary, `error`);
                window.location.reload();
              }
              break;
            case `product cannot be bought`:
              await window.$swal(``, window.vueCartHelperTexts.errorCannotBeBoughtTemporary, `error`);
              break;
            default:
              if((err as FetchError).errorCode === 11018) {
                await window.$swal(``, err.message, `error`);
              } else {
                await window.$swal(``, window.vueCartHelperTexts.errorUnknown, `error`);
              }
          }
          throw err;
        }
      } else {
        await window.$swal(``, window.vueCartHelperTexts.errorUnknown, `error`);
        throw new ApplicationError(window.vueCartHelperTexts.errorUnknown);
      }
    }
  }

  public async addItemToCartWithProductInfo<T extends IShoppingCartItem = IShoppingCartItem>(productInfo: IProduct, isPartner = false, amount?: number, cartId?: number): Promise<T | null> {
    const body: { amount: number; cartId?: number; matchcode: string } = {
      amount: amount ?? 1,
      matchcode: productInfo.matchcode,
    };

    // TODO WEB-3493 in many places the default value is -1, therefore we need this extra check here
    body.cartId = cartId && cartId >= 0
      ? cartId
      : CookieHelper.getLastUsedCartId();

    const addItemResponse = await this.addItemToCart(body, body.cartId);

    const atcProducts = {
      id: productInfo.matchcode,
      name: productInfo.title,
      price: ProductHelper.getPriceUnformatted(isPartner, productInfo.prices),
      quantity: amount ?? 1,
    };

    DataLayerHelper.setProductInfoOnAddToCartToGtm(atcProducts);

    const shoppingCart = addItemResponse.shoppingCarts.find(addToCartShoppingCart => addToCartShoppingCart.cartId === cartId);
    if(!shoppingCart) {
      return null;
    }
    return (shoppingCart.items as T[]).find(addToCartItem => addToCartItem.cartContentId === addItemResponse.cartContentId) ?? null;
  }

  public async addSupplement(cartId: number, supplementId: number, isPartnerApplication = false): Promise<IShoppingCart> {
    return this.zApiService.addSupplement(cartId, supplementId, isPartnerApplication);
  }

  public async createCustomerCart(customerNumber: string | number | null): Promise<ICreateShoppingCart & IShoppingCart> {
    try {
      const cart = await this.zApiService.postCartCreate(ECartTypeId.PERSONAL_USE, false, customerNumber ? +customerNumber : undefined);
      CookieHelper.setLastUsedCartId(cart.cartId);
      CookieHelper.setCartSession(cart.session);
      await this.setCart(cart);
      return cart;
    } catch (err) {
      if(err instanceof FetchError && err.errorCode === ErrorCodes.INFOWORX_RESPONSE_EMAIL_MISSING) {
        throw new Error(`cart:emailMissing`);
      } else if(err instanceof FetchError && err.errorCode === ErrorCodes.LIMITING_BOUNCER_ACTIVE) {
        SessionStorageHelper.setLimitingActive();
        throw err;
      } else {
        throw new Error(`cart:customerCartNotCreated`);
      }
    }
  }

  public async createNewCart(): Promise<void> {
    try {
      const cart = await this.zApiService.postCartCreate();
      CookieHelper.setLastUsedCartId(cart.cartId);
      CookieHelper.setPersonalUseCartId(cart.cartId);
      CookieHelper.setCartSession(cart.session);
      LocalStorageHelper.setCart(cart);
      await this.fetchCart();
    } catch (err) {
      if(err instanceof FetchError && err.errorCode === ErrorCodes.LIMITING_BOUNCER_ACTIVE) {
        SessionStorageHelper.setLimitingActive();
      } else {
        throw err;
      }
    }
  }

  public async deleteCustomerCart(cartId: number, cartTypeId: ECartTypeId): Promise<void> {
    // TODO WEB-3452 - refactor error handling, don't just throw an new Error()
    const success = await this.zApiService.deleteCart(cartId);
    if(success) {
      // recreate the PERSONAL_USE cart if it was deleted
      if(cartTypeId === ECartTypeId.PERSONAL_USE) {
        await this.createNewCart();
      }
      CookieHelper.removeCartId();
      await this.fetchCart();
    } else {
      throw new Error();
    }
  }

  public async fetchCart(cartIds: number[] = []): Promise<void> {
    if(!CookieHelper.getCartSession() && !CookieHelper.getAuthToken()) {
      return;
    }

    const cart = await this.zApiService.getCarts(cartIds.toString());

    if(!cart) {
      await this.createNewCart();
    } else {
      await this.setCart(cart);
    }
  }

  public async fetchCoupons(cn: string, genericAutoredeem = false): Promise<ICustomerCoupon[]> {
    const coupons = await this.zApiService.getCoupons(+cn, genericAutoredeem);
    return TransformHelper.transformApiCouponsToCustomerCoupons(coupons);
  }

  public async fetchDeliveryAddresses(cartId: string, mergeAddresses: ICustomerAddress[]): Promise<IAddressData> {
    try {
      const partnerShopInfo = LocalStorageHelper.getPartnerShopInformation();
      const checkoutAddresses = await this.zApiService.checkoutAddresses(
        cartId,
        partnerShopInfo?.customerNumber ? partnerShopInfo.customerNumber.toString() : undefined,
      );
      const addresses = checkoutAddresses;
      addresses.addresses = [
        ...mergeAddresses,
        ...(checkoutAddresses.addresses.map(address => AddressHelper.transformAddressToCustomerAddress(address))),
      ];
      LocalStorageHelper.setDeliveryAddresses(addresses);
      return checkoutAddresses;
    } catch (err) {
      if(err instanceof Error) {
        // TODO WEB-3452 - refactor error handling, don't just throw an new Error()
        ErrorHandlerHelper.handleError(err, { logToConsole: true });
        throw new Error(err.message);
      } else {
        throw err;
      }
    }
  }

  public async getCarts(): Promise<IShoppingCartIndividual[]> {
    await this.loadOrFetchCart();
    if(!this.cart) {
      try {
        const bouncerConfig = await this.zApiService.getBouncerConfig();
        if(bouncerConfig.active) {
          this.cart = CartHelper.getEmptyCart();
        }
      } catch (err) {
        ErrorHandlerHelper.handleError(err, { logToConsole: true });
        return [];
      }
    }

    return this.cart?.shoppingCarts ?? [];
  }

  public async getCartsForCheckout(cartIds: number[]): Promise<IShoppingCartIndividual[]> {
    await this.fetchCart(cartIds);
    return this.cart?.shoppingCarts ?? [];
  }

  public async hasMultipleCarts(): Promise<boolean> {
    await this.loadOrFetchCart();
    return (this.cart?.shoppingCarts ?? []).length > 1;
  }

  public async loadOrFetchCart(): Promise<IShoppingCart> {
    if(this.cart) {
      return this.cart;
    }

    this.cart = LocalStorageHelper.getCart();
    if(!this.cart) {
      await this.fetchCart();
    }

    if(!this.cart) {
      CookieHelper.removeCart();
      await this.createNewCart();
    }

    return this.cart as IShoppingCart;
  }

  public mergeModifiedCartIntoExistingCarts(modifiedCart: IShoppingCart): void {
    if(!modifiedCart || !this.cart) {
      return;
    }
    const cart = modifiedCart.shoppingCarts[0];
    const indexOfExistingCartToBeModified = this.cart.shoppingCarts.findIndex(shoppingCart => shoppingCart.cartId === cart.cartId);
    if(indexOfExistingCartToBeModified !== -1) {
      this.cart.shoppingCarts.splice(indexOfExistingCartToBeModified, 1, cart);
      return;
    }

    this.cart = modifiedCart;
  }

  public async placeSetInCart(setMatchcode: string, itemMatchcode: string, cartId: number): Promise<IShoppingCart> {
    const shoppingCart = await this.zApiService.addItemToCart({ matchcode: setMatchcode, setSwap: true }, cartId);

    const cart = shoppingCart.shoppingCarts.find(tmpCart => tmpCart.cartId === cartId);
    const item = cart?.items.find((singleItem): boolean => singleItem.matchcode === itemMatchcode);
    const set = cart?.items.find((singleItem): boolean => singleItem.matchcode === setMatchcode);

    TrackingHelper.trackCartRemoveItem(setMatchcode, shoppingCart.sum.total);
    TrackingHelper.trackCartRemoveItem(itemMatchcode, shoppingCart.sum.total);

    if(item) {
      TrackingHelper.trackCartAddItem(
        item.matchcode,
        item.title ?? ``,
        ``,
        parseFloat(item.price.normal.toFixed(2)),
        item.amount,
        shoppingCart.sum.total ? parseFloat(shoppingCart.sum.total.toFixed(2)) : -1,
      );
    }

    if(set) {
      TrackingHelper.trackCartAddItem(
        set.matchcode,
        set.title ?? ``,
        ``,
        parseFloat(set.price.normal.toFixed(2)),
        set.amount,
        shoppingCart.sum.total ? parseFloat(shoppingCart.sum.total.toFixed(2)) : -1,
      );
    }

    return shoppingCart;
  }

  public async redeemCoupon(cartId: number, code: string, isPartnerApplication = false): Promise<IShoppingCart> {
    return this.zApiService.redeemCoupon(cartId, code, isPartnerApplication);
  }

  public async removeCoupon(cartId: number, cartContentId: number, code: string, isPartnerApplication = false): Promise<IShoppingCart> {
    return this.zApiService.removeCoupon(cartContentId, cartId, code, isPartnerApplication);
  }

  public async removeItemFromCart(
    cartId: number,
    cartContentId: number,
    matchcode: string,
    isPartnerApplication = false,
    type?: TCartItemType,
  ): Promise<IShoppingCart> {
    const cart = await this.zApiService.removeItemFromCart(cartContentId, cartId, isPartnerApplication, type);
    if(type === `product`) {
      TrackingHelper.trackCartRemoveItem(matchcode, cart.sum.total);
    }
    CartHelper.updateLocalCart(cart.shoppingCarts[0]);
    return cart;
  }

  public async setCart(newCart: IShoppingCart): Promise<void> {
    this.cart = newCart;

    let itemsCount = 0;
    this.cart.shoppingCarts = this.cart.shoppingCarts.map(cart => {
      if(cart.cartTypeId !== ECartTypeId.PARTNER_APPLICATION) {
        itemsCount += cart.itemsCount;
      }
      return {
        ...cart,
        items: cart.items.sort((a, b) => (a.title ?? ``).localeCompare(b.title ?? ``)),
      };
    });

    CookieHelper.setLastUsedCartId(this.cart.shoppingCarts[0].cartId);

    const ownCartId = newCart.shoppingCarts.find(cart => cart.cartTypeId === config.cartTypes.EIGENBEDARF)?.cartId;
    if(ownCartId) {
      CookieHelper.setPersonalUseCartId(ownCartId);
    } else {
      CookieHelper.removePersonalUseCartId();
    }

    LocalStorageHelper.setCart(this.cart);
    CartHelper.updateCartCount(itemsCount);
  }

  public setTaxNumberForOwnCart(taxNumber: string): void {
    if(!this.cart) {
      return;
    }
    for(const cart of this.cart.shoppingCarts) {
      if(cart.cartTypeId === ECartTypeId.PERSONAL_USE) {
        cart.taxNumber = taxNumber;
      }
    }
  }

  public async updateItem(data: IPatchCartItemRequestData, isPartnerApplication = false): Promise<IShoppingCart> {
    let cart: IShoppingCart;
    try {
      cart = await this.zApiService.updateCartItem(data, isPartnerApplication);
    } catch (err) {
      throw new Error(err.message);
    }
    return cart;
  }

  public async updateQuantity(cartId: number, item: IShoppingCartItem, isPartnerApplication = false, type: TCartItemType = `product`): Promise<IShoppingCart | undefined> {
    if(item.amount < 1) {
      return this.removeItemFromCart(cartId, item.cartContentId, item.matchcode, isPartnerApplication, type);
    }

    let cart;
    try {
      cart = await this.zApiService.updateQuantity(item.amount, item.cartContentId, isPartnerApplication, item.matchcode);
      if(type === `product`) {
        TrackingHelper.trackCartRemoveItem(item.matchcode, cart.sum.total);
        TrackingHelper.trackCartAddItem(
          item.matchcode,
          item.title ?? ``,
          ``,
          parseFloat(item.price.normal.toFixed(2)),
          item.amount,
          cart.sum.total ? parseFloat(cart.sum.total.toFixed(2)) : -1,
        );
      }
      CartHelper.updateLocalCart(cart.shoppingCarts[0]);
    } catch (err) {
      if(err instanceof FetchError && new RegExp(`^Dieser Artikel kann maximal `, `ig`).test(err.message)) {
        const regexp = /^Dieser Artikel kann maximal ([0-9]*)-/ig;
        const dataArr = regexp.exec(err.message) as string[];
        throw new ApplicationError(`${window.vueCartHelperTexts.errorMaxUnitsPerCart.part1}${dataArr[1]}${window.vueCartHelperTexts.errorMaxUnitsPerCart.part2}`);
      } else {
        throw err;
      }
    }
    return cart;
  }
}
